/* eslint-disable no-useless-escape */
import {useEffect, useState} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material';
import './ArrFilterAccordion.scss';
import {DocMetadataFilter} from '../../../../shared/interfaces';
import ArrFilterChip from '../../arr-filter-chip/ArrFilterChip';
import ArrButton from '../../arr-button/ArrButton';
import {t} from 'i18next';
import FilterSelectionModal, {
  FILTER_SELECTION_MAP,
} from '../filter-selection-modal/FilterSelectionModal';
import _ from 'lodash';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {ReactComponent as ChevronIcon} from '../../../../assets/images/chevron-base-icon.svg';
import ArrTooltipWrapper from '../../arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrSelectionChip, {
  ArrSelectionChipData,
} from '../../arr-selection-chip/ArrSelectionChip';

export type filterSelectionType =
  | 'include'
  | 'exclude'
  | 'before'
  | 'after'
  | 'on'
  | 'between'
  | 'below'
  | 'over'
  | 'at';

type ArrFilterAccordionProps = {
  filters: DocMetadataFilter[];
  onApplyAllClick?: (param: ActiveFilter[]) => void;
  isFilterInactive: boolean;
  expand?: boolean;
  title: string;
  tooltipTitle?: string;
  tooltipText?: string;
  isApplyFilterButtonVisible?: boolean;
  onSetActiveFilters?: (param: ActiveFilter[]) => void;
  preActiveFilters: PreActiveFilters | null | undefined;
  dataTestId: string;
};
type FilterValue = {
  disabled: boolean;
  group: string | null;
  selected: boolean;
  text: string;
  value: string;
};
export type ActiveFilter = {
  id: string;
  type: 'NVARCHAR' | 'DATETIME' | 'INT';
  label: string;
  values?: FilterValue[];
  selectionType?: filterSelectionType;
  seperator?: string;
  startDate?: string;
  endDate?: string;
  isSelected?: boolean;
  startSize?: string;
  endSize?: string;
};

export type PreActiveFilters = [
  {
    fieldID: string;
    fieldName: string;
    literalValue: string;
    fieldType: string;
  },
];

const ArrFilterAccordion: React.FC<ArrFilterAccordionProps> = ({
  filters,
  onApplyAllClick,
  isFilterInactive,
  expand = false,
  title,
  isApplyFilterButtonVisible = true,
  onSetActiveFilters,
  preActiveFilters,
  dataTestId = 'arr-filter-accordion',
}) => {
  const [parentFilters, setParentFilters] = useState<ArrSelectionChipData[]>(
    []
  );
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [isFilterUpdating, setIsFilterUpdating] = useState<boolean>(false);
  const [expanded, setExpanded] = useState(expand);
  const [openDropdownFocusEl, setOpenDropdownFocusEl] =
    useState<HTMLElement | null>(null);

  const [filterModalState, setFilterModalState] = useState<{
    anchorEl: HTMLElement | null;
    selectedFilter: ActiveFilter | null;
  }>({anchorEl: null, selectedFilter: null});
  const getFilterInfo = (
    id: string,
    filterType: 'parent' | 'active'
  ): ActiveFilter => {
    if (filterType == 'parent') {
      const parentFilter =
        parentFilters.find((filter) => filter.id == id) ??
        ({} as ArrSelectionChipData);

      return {
        id: parentFilter.id,
        label: parentFilter.label,
        type: parentFilter.type,
        isSelected: parentFilter.isSelected,
      } as ActiveFilter;
    }
    return _.cloneDeep(
      activeFilters.find((filter) => filter.id == id) ?? ({} as ActiveFilter)
    );
  };
  const updateParentFilter = (id: string, isSelected: boolean) => {
    setIsFilterUpdating(true);
    setParentFilters((prev) => {
      return prev.map((filter) => {
        if (filter.id == id) {
          return {...filter, isSelected: isSelected};
        }
        return filter;
      });
    });
  };
  const getActiveFiltersChip = () => {
    return activeFilters.map((filter) => {
      if (filter.type == 'NVARCHAR') {
        return (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? 'include'],
            ]}
            selections={
              filter.values?.map((value) => {
                return {id: value.text, value: `\"${value.text}\"`};
              }) ?? []
            }
            variant="v2"
            isResponsive={false}
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      } else if (filter.type == 'DATETIME') {
        const selections: {id: string; value: string}[] = [];

        if (filter.startDate) {
          selections.push({id: filter.startDate, value: filter.startDate});
        }
        if (filter.endDate) {
          selections.push({id: filter.endDate, value: filter.endDate});
        }

        return (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? ''],
            ]}
            selections={[...selections]}
            variant="v2"
            isResponsive={false}
            operator={selections.length === 2 ? 'to' : undefined}
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      } else if (filter.type == 'INT') {
        const selections: {id: string; value: string}[] = [];

        if (filter.startSize) {
          selections.push({id: filter.startSize, value: filter.startSize});
        }
        if (filter.endSize) {
          selections.push({id: filter.endSize, value: filter.endSize});
        }

        return (
          <ArrFilterChip
            key={filter.id}
            classNames="active-filter"
            filterName={[
              filter.label,
              FILTER_SELECTION_MAP[filter.selectionType ?? ''],
            ]}
            selections={[...selections]}
            variant="v2"
            isResponsive={false}
            operator={selections.length === 2 ? 'to' : undefined}
            clickable
            id={filter.id}
            onClick={activeFilterClickHandler}
            onRemove={clearActiveFilterHandler}
            dataTestId={filter.label?.replaceAll(' ', '').toLowerCase()}
          />
        );
      }
    });
  };

  const parentFilterClickHandler = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    filterData: ArrSelectionChipData
  ) => {
    let targetElement: HTMLElement | null = null;

    if ('currentTarget' in event) {
      targetElement = event.currentTarget;
    }

    if (event.type === 'click' || ('key' in event && (event.key === 'Enter' || event.code === 'Space'))) {
      event.preventDefault();
      if (filterModalState.selectedFilter?.id == filterData.id) return;
      const flterInfo: ActiveFilter = getFilterInfo(filterData.id, 'parent');
      if (flterInfo.isSelected) return;

      setFilterModalState({
        anchorEl: targetElement,
        selectedFilter: {...flterInfo},
      });
      }
  };
  const activeFilterClickHandler = (
    element: HTMLElement | null,
    id?: string
  ) => {
    setIsFilterUpdating(true);
    if (element) {
      setFilterModalState({
        anchorEl: element,
        selectedFilter: {...getFilterInfo(id ?? '', 'active')},
      });
    }
  };
  const modalCloseHandler = () => {
    setFilterModalState({
      anchorEl: null,
      selectedFilter: null,
    });
    setIsFilterUpdating(false);
     setTimeout(() => {
        openDropdownFocusEl?.focus();
      }, 100);
      setOpenDropdownFocusEl(null);
  };
  const onAddToFiltersHandler = (filterData: ActiveFilter) => {
    const existingActiveFilter = activeFilters.find(
      (filter) => filter.id == filterData.id
    );
    let updatedActiveFilters = activeFilters.map((filter) => {
      if (filter.id == filterData.id) return _.cloneDeep(filterData);
      return filter;
    });
    if (!existingActiveFilter) {
      updatedActiveFilters = [...updatedActiveFilters, _.cloneDeep(filterData)];
    }
    setActiveFilters([...updatedActiveFilters]);
    onSetActiveFilters?.([...updatedActiveFilters]);
    onApplyAllClick?.([...updatedActiveFilters]);
    updateParentFilter(filterData.id, true);
    modalCloseHandler();
  };
  const clearAllHandler = () => {
    modalCloseHandler();
    setActiveFilters([]);
    setParentFilters((prev) => {
      return prev.map((filter) => {
        return {...filter, isSelected: false};
      });
    });
    onSetActiveFilters?.([]);
    onApplyAllClick?.([]);
  };
  const clearActiveFilterHandler = (id?: string) => {
    if (id == filterModalState.selectedFilter?.id) {
      modalCloseHandler();
    }
    setActiveFilters((prev) => {
      const filterData = prev.filter((filter) => filter.id != id);
      if (!filterData.length) {
        onSetActiveFilters?.([]);
        onApplyAllClick?.([]);
      } else {
        onSetActiveFilters?.([...filterData]);
        onApplyAllClick?.([...filterData]);
      }
      return filterData;
    });
    setParentFilters((prev) => {
      return prev.map((filter) => {
        if (filter.id == id) {
          return {...filter, isSelected: false};
        }
        return {...filter};
      });
    });
  };

  useEffect(() => {
    const mapedParentFilters = filters.map((data) => {
      return {
        id: data.projectFieldId,
        label: data.projectFieldLabel,
        type: data.dataType,
        isSelected: false,
      } as ArrSelectionChipData;
    });
    setParentFilters(mapedParentFilters);
  }, [filters]);

  useEffect(() => {
    if (isFilterInactive) {
      for (const item of parentFilters) {
        item.isSelected = false;
      }
      setActiveFilters([]);
    }
  }, [isFilterInactive]);

   useEffect(() => {
    function moveFocusOnCloseBtn(e: KeyboardEvent) {
      if (
        e.key === 'Tab' &&
        filterModalState.anchorEl &&
        !openDropdownFocusEl
      ) {
        e.preventDefault();
        (
          document.querySelector(
            '[data-testid="filter-selection-close-button"]'
          ) as HTMLElement
        )?.focus();
        setOpenDropdownFocusEl(filterModalState.anchorEl);
      }
    }

    window.addEventListener('keydown', moveFocusOnCloseBtn);
    return () => window.removeEventListener('keydown', moveFocusOnCloseBtn);
  }, [filterModalState.anchorEl, openDropdownFocusEl]);

  const inferSelectionType = (
    literalValue: string,
    dataType: 'NVARCHAR' | 'DATETIME' | 'INT'
  ): filterSelectionType => {
    const l = (literalValue || '').toUpperCase();

    if (dataType === 'NVARCHAR') {
      if (/\bNOT IN\b/.test(l)) return 'exclude';
      if (/\bIN\b/.test(l)) return 'include';
      if (/=/.test(l)) return 'include';
      return 'include';
    }

    if (dataType === 'DATETIME') {
      if (/\bBETWEEN\b/.test(l)) return 'between';
      if (/\bLESSTHAN\b/.test(l)) return 'before';
      if (/\bGREATERTHAN\b/.test(l)) return 'after';
      if (/=/.test(l)) return 'on';
      if (/\bNOT IN\b/.test(l)) return 'exclude';
      if (/\bIN\b/.test(l)) return 'include';
      return 'on';
    }

    if (dataType === 'INT') {
      if (/\bBETWEEN\b/.test(l)) return 'between';
      if (/\bLESSTHAN\b/.test(l)) return 'below';
      if (/\bGREATERTHAN\b/.test(l)) return 'over';
      if (/=/.test(l)) return 'at';
      if (/\bNOT IN\b/.test(l)) return 'exclude';
      if (/\bIN\b/.test(l)) return 'include';
      return 'at';
    }

    return 'include';
  };

  const extractOperands = (literalValue: string): string[] => {
    const operands: string[] = [];

    const quotedRegex = /N?'([^']*)'|N?"([^"]*)"/g;
    let qm: RegExpExecArray | null;
    while ((qm = quotedRegex.exec(literalValue)) !== null) {
      const val = qm[1] ?? qm[2];
      if (val != null && val.length) operands.push(val);
    }

    if (operands.length === 0) {
      // eslint-disable-next-line security/detect-unsafe-regex
      const numRegex = /(-?\d+(?:\.\d+)?)/g;
      let nm: RegExpExecArray | null;
      while ((nm = numRegex.exec(literalValue)) !== null) {
        operands.push(nm[1]);
      }
    }

    return operands;
  };

  const buildActiveFilters = (
    pre: PreActiveFilters,
    docFilters: DocMetadataFilter[]
  ): ActiveFilter[] => {
    const result: ActiveFilter[] = [];

    const byId = new Map<string, DocMetadataFilter>();
    const byLabel = new Map<string, DocMetadataFilter>();
    docFilters.forEach((f) => {
      byId.set(String(f.projectFieldId), f);
      byLabel.set(f.projectFieldLabel, f);
    });

    pre.forEach((fb) => {
      const def = byId.get(String(fb.fieldID)) ?? byLabel.get(fb.fieldName);
      if (!def) return;

      const selectionType = inferSelectionType(
        fb.literalValue,
        def.dataType as 'NVARCHAR' | 'DATETIME' | 'INT'
      );
      const operands = extractOperands(fb.literalValue);

      const base: ActiveFilter = {
        id: def.projectFieldId,
        label: def.projectFieldLabel,
        type: def.dataType as 'NVARCHAR' | 'DATETIME' | 'INT',
        isSelected: true,
        selectionType,
      };

      if (def.dataType === 'NVARCHAR') {
        const values: FilterValue[] = operands.map((v) => ({
          disabled: false,
          group: null,
          selected: true,
          text: v,
          value: v,
        }));
        result.push({...base, values});
        return;
      }

      if (def.dataType === 'DATETIME') {
        const [v1, v2] = operands;
        if (selectionType === 'between') {
          result.push({...base, startDate: v1 ?? '', endDate: v2 ?? ''});
        } else if (selectionType === 'before') {
          result.push({...base, endDate: v1 ?? ''});
        } else if (selectionType === 'after') {
          result.push({...base, startDate: v1 ?? ''});
        } else if (selectionType === 'on') {
          result.push({...base, startDate: v1 ?? ''});
        } else {
          result.push({...base, startDate: v1 ?? '', endDate: v2 ?? ''});
        }
        return;
      }

      if (def.dataType === 'INT') {
        const [v1, v2] = operands;
        if (selectionType === 'between') {
          result.push({...base, startSize: v1 ?? '', endSize: v2 ?? ''});
        } else if (selectionType === 'below') {
          result.push({...base, endSize: v1 ?? ''});
        } else if (selectionType === 'over') {
          result.push({...base, startSize: v1 ?? ''});
        } else if (selectionType === 'at') {
          result.push({...base, startSize: v1 ?? ''});
        } else {
          result.push({...base, startSize: v1 ?? '', endSize: v2 ?? ''});
        }
      }
    });

    return result;
  };

  useEffect(() => {
    if (!preActiveFilters || !preActiveFilters.length || !filters?.length)
      return;

    const prebuilt = buildActiveFilters(preActiveFilters, filters);
    if (!prebuilt.length) return;

    setActiveFilters(prebuilt);
    onSetActiveFilters?.([...prebuilt]);
    onApplyAllClick?.([...prebuilt]);

    const selectedIds = new Set(prebuilt.map((f) => f.id));
    setParentFilters((prev) =>
      prev.map((pf) =>
        selectedIds.has(pf.id) ? {...pf, isSelected: true} : pf
      )
    );
  }, [preActiveFilters, filters]);

  const iconComponent = (
    <Box>
      <ChevronIcon className="expand-icon" />
    </Box>
  );
  return (
    <Box className="arr-filter-panel" data-test-id={'ArrFilterAccordion'}>
      <Accordion
        expanded={expanded}
        onChange={() =>
          setExpanded((prev) => {
            modalCloseHandler();
            return !prev;
          })
        }
      >
        <AccordionSummary expandIcon={iconComponent}>
          <Box className="accordion-summary-content">
            <Box className="filter-panel-header">
              <Box className="title-section">
                <Typography
                  className="typo-text subhead4_size title"
                  data-testid={`arr-filter-accordion-title-${dataTestId}`}
                  tabIndex={0}
                >
                  {title}
                </Typography>
                <ArrTooltipWrapper
                  data-testid="ceh-tooltip-wrapper"
                  title={t('docExplorer.filterAccordion.tooltip.title')}
                  closeTooltipOnEscPress
                  trigger={
                    <div
                      data-testid={`arr-filter-accordion-div-${dataTestId}`}
                    >
                      <InfoIcon
                        fontSize="small"
                        className="filter-panel-info-icon"
                        data-testid="info-icon"
                        aria-label='info-icon'
                        tabIndex={0}
                      />
                    </div>
                  }
                >
                  <div data-testid="ceh-tooltip-content">
                    <p
                      data-testid={`arr-filter-accordion-content-${dataTestId}`}
                      tabIndex={0}
                    >
                      {t('docExplorer.filterAccordion.tooltip.content')}
                    </p>
                    <p
                      data-testid={`arr-filter-accordion-content1-${dataTestId}`}
                      tabIndex={0}
                    >
                      {t('docExplorer.filterAccordion.tooltip.content1')}
                    </p>
                  </div>
                </ArrTooltipWrapper>
              </Box>
            </Box>

            <Box className="expand-icon-text-section">
              <Typography
                className=" typo-text buttonSmall_size  title"
                data-testid={`arr-filter-accordion-expand-text-${dataTestId}`}
                tabIndex={0}
              >
                {expanded
                  ? t('arrFilterAccordion.hide')
                  : t('arrFilterAccordion.show')}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="arr-filter-panel-body" tabIndex={0}>
            <Box className="parent-filter-container">
              {parentFilters.map((data) => (
                <ArrSelectionChip
                  key={data.id}
                  filterData={data}
                  onClick={parentFilterClickHandler}
                  isFilterInactive={isFilterInactive}
                  variant="v2"
                  tabIndex={-1}
                  dataTestId={data.label?.replaceAll(' ', '').toLowerCase()}
                />
              ))}
            </Box>

            {
              <Box className="sub-filter-container">
                <Box className="active-filter-section">
                  <Box className="active-filter-header-section">
                    <Typography
                      className="typo-text subhead4_size title"
                      data-testid={`arr-filter-accordion-active-filters`}
                      tabIndex={0}
                    >
                      {t('arrFilterAccordion.activeFilters')}
                    </Typography>
                    {activeFilters.length != 0 && (
                      <Box className="active-filter-section-button">
                        <ArrButton
                          dataTestId="clearAllHandlerButton"
                          aria-label={t('common.button.clearAll')}
                          size="small"
                          variant="text"
                          onClick={clearAllHandler}
                          className="clear-all-button"
                          tabIndex={0}
                        >
                          {t('common.button.clearAll')}
                        </ArrButton>
                        {isApplyFilterButtonVisible && (
                          <ArrButton
                            dataTestId="applyFiltersHandlerButton"
                            aria-label={t('arrFilterAccordion.applyFilters')}
                            size="small"
                            onClick={() => {
                              onSetActiveFilters?.([]);
                              onApplyAllClick?.([]);
                            }}
                            tabIndex={0}
                          >
                            {t('arrFilterAccordion.applyFilters')}
                          </ArrButton>
                        )}
                      </Box>
                    )}
                  </Box>
                  {activeFilters.length != 0 ? (
                    <Box className="active-filter-filter-section" tabIndex={0}>
                      {getActiveFiltersChip()}
                    </Box>
                  ) : (
                    <Box className="none-selected-section">
                      <Typography
                        className="typo-text body3_size none-selected-text"
                        data-testid={`arr-filter-accordion-none-selected`}
                        tabIndex={0}
                      >
                        {t('arrFilterAccordion.noneSelected')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            }
          </Box>
        </AccordionDetails>
      </Accordion>
      <FilterSelectionModal
        anchorElement={filterModalState.anchorEl}
        onClose={modalCloseHandler}
        filterSelectionData={
          filterModalState.selectedFilter ?? ({} as ActiveFilter)
        }
        onAddToFilters={onAddToFiltersHandler}
        isUpdateModel={isFilterUpdating}
        closeOnEsc
      />
    </Box>
  );
};

export default ArrFilterAccordion;
